import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Container,
  Paper,
  IconButton,
  Zoom,
  Stack,
  Alert,
  useTheme as useMuiTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField from '@mui/material/TextField';
import { FiTarget } from "react-icons/fi";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { useUserPermissions } from "../components/useUserPermissions.tsx";
import { Tooltip } from "@mui/material";
import { FaCrown } from "react-icons/fa";
import StarPurple500Icon from '@mui/icons-material/StarPurple500'; 
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'; 
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'; 
import { useThemeContext } from '../contexts/ThemeContext.tsx';

// --- STYLED COMPONENTS ---

interface StyledProps {
  darkMode?: boolean;
}

const AnimatedDialog = styled(Dialog, {
  // Megakadályozzuk, hogy a darkMode prop lejusson a DOM-ig
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    background: darkMode 
      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
      : 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    color: darkMode ? 'white' : '#1a1a2e',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
    padding: theme.spacing(1),
  },
}));

const DialogActionButton = styled(Button, {
  // Megakadályozzuk, hogy a darkMode prop lejusson a DOM-ig
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  borderRadius: '12px',
  padding: '10px 24px',
  fontWeight: 'bold',
  textTransform: 'none',
  background: darkMode 
    ? 'linear-gradient(45deg, #00d2ff, #3a7bd5)' 
    : theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    background: darkMode 
      ? 'linear-gradient(45deg, #3a7bd5, #00d2ff)' 
      : theme.palette.primary.dark,
  }
}));

const GameContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
}));

const GameArea = styled(Paper)(({ theme }) => ({
  flex: 1,
  position: "relative",
  margin: theme.spacing(2),
  overflow: "hidden",
  background: theme.palette.mode === 'dark' ? '#0f0f1b' : '#f9fafb',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px'
}));

const Target = styled(IconButton)(({ size }) => ({
  position: "absolute",
  transform: "translate(-50%, -50%)",
  fontSize: size === "small" ? "24px" : size === "medium" ? "36px" : "48px",
}));

const CountdownOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  zIndex: 1000,
});

// --- INTERFACES ---

interface SpecialIcon {
  id: number;
  type: 'star' | 'token' | 'penalty' | 'target'; 
  position: { x: number; y: number };
}

interface GameState {
  isPlaying: boolean;
  timeLeft: number;
  score: number;
  countdown: number | null;
}

// --- MAIN COMPONENT ---

const AimTesterGame = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useThemeContext();
  const navigate = useNavigate();

  // Auth check inside component to be reactive
  const isAuthenticated = !!Cookies.get("token");

  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  
  const [gameMode, setGameMode] = useState("");
  const [settings, setSettings] = useState({
    duration: "60",
    customDuration: "30",
    size: "medium",
  });
  const [gameState, setGameState] = useState<GameState>({
    isPlaying: false,
    timeLeft: 0,
    score: 0,
    countdown: null,
  });
  const [targetPosition, setTargetPosition] = useState({ x: 50, y: 50 });
  const [gameOverDialogOpen, setGameOverDialogOpen] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [highScore, setHighScore] = useState<number | null>(null);
  const { hasPermission, loading: permissionsLoading } = useUserPermissions();
  const [tokens, setTokens] = useState(0);
  const [clickCounter, setClickCounter] = useState(0);

  const [specialIcons, setSpecialIcons] = useState<SpecialIcon[]>([]);
  const [adamantineTargets, setAdamantineTargets] = useState<SpecialIcon[]>([]);

  const generateUniqueId = () => Date.now() + Math.random();

  const generateNewPosition = useCallback(() => {
    const x = Math.random() * 90 + 5;
    const y = Math.random() * 90 + 5;
    setTargetPosition({ x, y });
  }, []);

  const startGame = () => {
    const duration = settings.duration === 'custom' 
    ? parseInt(settings.customDuration)
    : parseInt(settings.duration);
    
    setWelcomeOpen(false);
    setConfigOpen(false);
    setLoginPromptOpen(false);
    setSpecialIcons([]);
    setAdamantineTargets([]);
    setTokens(0);
    setClickCounter(0);
    
    setGameState({
      isPlaying: true,
      timeLeft: gameMode === "AdamantineParagon" ? 60 : duration,
      score: 0,
      countdown: 3,
    });
    
    if (gameMode === "AdamantineParagon") {
      const initialTargets = Array.from({ length: 8 }, () => ({
        position: { x: Math.random() * 90 + 5, y: Math.random() * 90 + 5 },
        type: 'target' as const,
        id: generateUniqueId()
      }));
      setAdamantineTargets(initialTargets);
    } else {
      generateNewPosition();
    }
  };

  const handleTargetClick = () => {
    if (gameState.isPlaying && gameState.countdown === null) {
      let newScore = gameState.score + 1;
      let newClickCounter = clickCounter + 1;
      let newSpecialIcons = [...specialIcons];

      // Jogosultság alapú speciális logika
      if (gameMode === "CopperApprentice") {
        if (newClickCounter % 10 === 0) {
          newSpecialIcons.push({
            type: 'star',
            position: { x: Math.random() * 90 + 5, y: Math.random() * 90 + 5 },
            id: generateUniqueId()
          });
        }
      }

      if (gameMode === "AmberSentinel") {
        if (newClickCounter % 20 === 0) {
          newSpecialIcons.push({
            type: 'token',
            position: { x: Math.random() * 90 + 5, y: Math.random() * 90 + 5 },
            id: generateUniqueId()
          });
        }
        // Büntetés esélye
        if (Math.random() < 0.3) {
          newSpecialIcons.push({
            type: 'penalty',
            position: { x: Math.random() * 90 + 5, y: Math.random() * 90 + 5 },
            id: generateUniqueId()
          });
        }
      }

      setClickCounter(newClickCounter);
      setSpecialIcons(newSpecialIcons);
      setGameState(prev => ({ ...prev, score: newScore }));
      generateNewPosition();
    }
  };

  const handleSpecialIconClick = (icon: SpecialIcon) => {
    if (gameMode === "CopperApprentice" && icon.type === 'star') {
      setGameState(prev => ({ ...prev, score: prev.score + 2 }));
    }
    if (gameMode === "AmberSentinel") {
      if (icon.type === 'token') setTokens(prev => prev + 1);
      else if (icon.type === 'penalty') setTokens(prev => Math.max(0, prev - 1));
    }
    if (gameMode === "AdamantineParagon") {
      switch(icon.type) {
        case 'star': setGameState(prev => ({ ...prev, score: prev.score + 2 })); break;
        case 'token': setTokens(prev => prev + 1); break;
        case 'penalty': setTokens(prev => Math.max(0, prev - 1)); break;
      }
    }
    setSpecialIcons(prev => prev.filter(i => i.id !== icon.id));
  };

  const handleAdamantineClick = (target: SpecialIcon) => {
    setAdamantineTargets(prev => prev.filter(t => t.id !== target.id));
    if (target.type === 'target') {
      setGameState(prev => ({ ...prev, score: prev.score + 1 }));
      setAdamantineTargets(prev => [
        ...prev,
        {
          position: { x: Math.random() * 90 + 5, y: Math.random() * 90 + 5 },
          type: 'target' as const,
          id: generateUniqueId()
        }
      ]);
      // Adamantine módban is eshetnek speciális ikonok
      if (Math.random() < 0.2) {
        const types: ('star' | 'token' | 'penalty')[] = ['star', 'token', 'penalty'];
        const newIconType = types[Math.floor(Math.random() * types.length)];
        setSpecialIcons(prev => [
          ...prev,
          {
            type: newIconType,
            position: { x: Math.random() * 90 + 5, y: Math.random() * 90 + 5 },
            id: generateUniqueId()
          }
        ]);
      }
    }
  };

  // Token mentés a játék végén
  useEffect(() => {
    if (!gameState.isPlaying && gameState.score > 0 && (gameMode === "AmberSentinel" || gameMode === "AdamantineParagon")) {
      fetch(`${process.env.REACT_APP_API_URL}/v1/user/updatetoken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`
        },
        body: JSON.stringify({ price: tokens })
      }).catch(err => console.error("Token update error:", err));
    }
  }, [gameState.isPlaying]);

  // Visszaszámláló logika
  useEffect(() => {
    let timer: number;
    if (gameState.countdown !== null && gameState.countdown > 0) {
      timer = window.setInterval(() => {
        setGameState((prev) => ({
          ...prev,
          countdown: prev.countdown !== null ? prev.countdown - 1 : null
        }));
      }, 1000);
    } else if (gameState.countdown === 0) {
      setGameState((prev) => ({ ...prev, countdown: null }));
    }
    return () => clearInterval(timer);
  }, [gameState.countdown]);

  // Játékidő logika
  useEffect(() => {
    let timer: number; 
    if (gameState.isPlaying && gameState.countdown === null) {
      timer = setInterval(() => {
        setGameState((prev) => {
          if (prev.timeLeft <= 1) return { ...prev, isPlaying: false, timeLeft: 0 };
          return { ...prev, timeLeft: prev.timeLeft - 1 };
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameState.isPlaying, gameState.countdown]);

  // Score beküldés
  useEffect(() => {
    if (
      !gameState.isPlaying &&
      gameState.timeLeft === 0 &&
      gameState.score > 0 &&
      gameMode !== "test" &&
      isAuthenticated &&
      !scoreSubmitted
    ) {
      setScoreSubmitted(true);
      fetch(`${process.env.REACT_APP_API_URL}/v1/user/aimscore`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        body: JSON.stringify({ aimscore: gameState.score }),
      })
      .then(() => fetch(`${process.env.REACT_APP_API_URL}/v1/user/aimhighscore`, {
        headers: { Authorization: `Bearer ${Cookies.get("token")}` },
      }))
      .then((response) => response.json())
      .then((data) => setHighScore(data.highscore))
      .catch((err) => console.error(err));
      setGameOverDialogOpen(true);
    } else if (!gameState.isPlaying && gameState.timeLeft === 0 && gameState.score > 0 && gameMode === "test") {
        setGameOverDialogOpen(true);
    }
  }, [gameState.isPlaying, gameState.timeLeft, gameState.score, gameMode, scoreSubmitted, isAuthenticated]);

  const resetGame = () => {
    setScoreSubmitted(false);
    setGameOverDialogOpen(false);
    setGameState({ isPlaying: false, timeLeft: 0, score: 0, countdown: null });
    setSpecialIcons([]);
    setAdamantineTargets([]);
    setTokens(0);
    setClickCounter(0);
    setWelcomeOpen(true);
  };

  const renderSpecialIcons = () => (
    specialIcons.map((icon) => {
      let IconComponent;
      let color;
      switch(icon.type) {
        case 'star': IconComponent = StarPurple500Icon; color = "#ffeb3b"; break;
        case 'token': IconComponent = MonetizationOnIcon; color = "#4caf50"; break;
        case 'penalty': IconComponent = RadioButtonUncheckedIcon; color = "#f44336"; break;
        default: IconComponent = StarPurple500Icon; color = "white";
      }
      return (
        <IconButton
          key={icon.id}
          style={{
            position: 'absolute',
            left: `${icon.position.x}%`,
            top: `${icon.position.y}%`,
            color: color,
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}
          onClick={() => handleSpecialIconClick(icon)}
        >
          <IconComponent fontSize="large" />
        </IconButton>
      );
    })
  );

  return (
    <GameContainer sx={{ mt: '64px' }}>
      {(gameMode === "AmberSentinel" || gameMode === "AdamantineParagon") && (
        <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold' }}>Tokens: {tokens}</Typography>
      )}

      {/* Modern Welcome Dialog */}
      <AnimatedDialog 
        open={welcomeOpen && !gameState.isPlaying}
        TransitionComponent={Zoom}
        transitionDuration={400}
        darkMode={darkMode}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem', color: darkMode ? '#00d2ff' : 'primary.main' }}>
          Welcome to AIM Tester Game
        </DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" fullWidth>
            <FormLabel sx={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'text.secondary', mb: 1 }}>Choose Your Game Mode</FormLabel>
            <RadioGroup value={gameMode} onChange={(e) => setGameMode(e.target.value)}>
              <Stack spacing={1}>
                <FormControlLabel value="test" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="Test Round" />
                <FormControlLabel value="score" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="Score Round" />
                
                <Tooltip title={!hasPermission("CopperApprentice") ? "Requires at least CopperApprentice rank" : ""} arrow>
                  <span>
                    <FormControlLabel
                      value="CopperApprentice"
                      control={<Radio sx={{ color: '#97564A' }} />}
                      label={<Box display="flex" alignItems="center">CopperApprentice <FaCrown style={{ color: "#97564A", marginLeft: 8 }} /></Box>}
                      disabled={!isAuthenticated || permissionsLoading || !hasPermission("CopperApprentice")}
                    />
                  </span>
                </Tooltip>

                <Tooltip title={!hasPermission("AmberSentinel") ? "Requires at least AmberSentinel rank" : ""} arrow>
                  <span>
                    <FormControlLabel
                      value="AmberSentinel"
                      control={<Radio sx={{ color: '#2196f3' }} />}
                      label={<Box display="flex" alignItems="center">AmberSentinel <FaCrown style={{ color: "#2196f3", marginLeft: 8 }} /></Box>}
                      disabled={!isAuthenticated || permissionsLoading || !hasPermission("AmberSentinel")}
                    />
                  </span>
                </Tooltip>

                <Tooltip title={!hasPermission("AdamantineParagon") ? "Requires at least AdamantineParagon rank" : ""} arrow>
                  <span>
                    <FormControlLabel
                      value="AdamantineParagon"
                      control={<Radio sx={{ color: 'orange' }} />}
                      label={<Box display="flex" alignItems="center">AdamantineParagon <FaCrown style={{ color: "orange", marginLeft: 8 }} /></Box>}
                      disabled={!isAuthenticated || permissionsLoading || !hasPermission("AdamantineParagon")}
                    />
                  </span>
                </Tooltip>
              </Stack>
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button onClick={() => navigate(-1)} sx={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}>Back</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (gameMode === "test") {
                setConfigOpen(true);
                setWelcomeOpen(false);
              } else if (gameMode) {
                if (!isAuthenticated) {
                  setLoginPromptOpen(true);
                } else {
                  startGame();
                }
              }
            }}
            disabled={!gameMode}
          >
            Next
          </Button>
        </DialogActions>
      </AnimatedDialog>

      {/* Modern Round Configuration Dialog */}
      <AnimatedDialog 
        open={configOpen}
        TransitionComponent={Zoom}
        transitionDuration={400}
        darkMode={darkMode}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', color: darkMode ? '#00d2ff' : 'primary.main' }}>
          Test Round Configuration
        </DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel sx={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>Time Duration</FormLabel>
            <RadioGroup
              value={settings.duration}
              onChange={(e) => setSettings({ ...settings, duration: e.target.value })}
            >
              <FormControlLabel value="10" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="10 seconds" />
              <FormControlLabel value="30" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="30 seconds" />
              <FormControlLabel value="60" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="60 seconds" />
              <Box display="flex" alignItems="center" mt={1}>
                <FormControlLabel value="custom" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="Custom" />
                <TextField
                  type="number"
                  size="small"
                  value={settings.duration === 'custom' ? settings.customDuration : ''}
                  onChange={(e) => {
                    let value = Math.min(3600, Math.max(1, parseInt(e.target.value) || 1));
                    setSettings(prev => ({ ...prev, duration: 'custom', customDuration: value.toString() }));
                  }}
                  sx={{ 
                    ml: 1, width: 100, 
                    '& .MuiOutlinedInput-root': { 
                      color: darkMode ? 'white' : 'black', 
                      '& fieldset': { borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)' } 
                    } 
                  }}
                  disabled={settings.duration !== 'custom'}
                />
              </Box>
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel sx={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>Icon Size</FormLabel>
            <RadioGroup
              value={settings.size}
              onChange={(e) => setSettings({ ...settings, size: e.target.value })}
            >
              <Stack direction="row" spacing={2}>
                <FormControlLabel value="small" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="Small" />
                <FormControlLabel value="medium" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="Medium" />
                <FormControlLabel value="large" control={<Radio sx={{ color: darkMode ? 'white' : 'primary.main' }} />} label="Large" />
              </Stack>
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            variant="contained" 
            fullWidth 
            onClick={startGame}
            sx={{ borderRadius: '8px', background: darkMode ? 'linear-gradient(45deg, #00d2ff, #3a7bd5)' : undefined }}
          >
            Start Battle
          </Button>
        </DialogActions>
      </AnimatedDialog>

      {/* Authentication Required Dialog */}
      <AnimatedDialog 
        open={loginPromptOpen && !isAuthenticated} 
        onClose={() => setLoginPromptOpen(false)} 
        disableScrollLock 
        darkMode={darkMode}
        TransitionComponent={Zoom}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Authentication Required</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
            You need to be logged in to perform this action!
          </Alert>
          <Stack direction="row" spacing={2} justifyContent="center">
            <DialogActionButton darkMode={darkMode} onClick={() => navigate('/login')}>
              Sign In
            </DialogActionButton>
            <DialogActionButton darkMode={darkMode} onClick={() => navigate('/register')}>
              Sign Up
            </DialogActionButton>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => setLoginPromptOpen(false)} sx={{ color: darkMode ? 'white' : 'text.primary' }}>
            Maybe later
          </Button>
        </DialogActions>
      </AnimatedDialog>

      {/* Game Info Bar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" sx={{ color: muiTheme.palette.text.primary, fontWeight: 'bold' }}>
          Time: {gameState.timeLeft}s
        </Typography>
        <Typography variant="h6" sx={{ color: muiTheme.palette.primary.main, fontWeight: 'bold' }}>
          Score: {gameState.score}
          {isAuthenticated && highScore !== null && ` | Best: ${highScore}`}
        </Typography>
        <Typography variant="h6" sx={{ color: muiTheme.palette.text.secondary }}>
            Mode: {gameMode ? gameMode.replace(/([A-Z])/g, ' $1').trim() : "Select Mode"}
        </Typography>
      </Box>

      <GameArea>
        {!gameState.isPlaying && !gameState.countdown && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Button 
              variant="contained" 
              onClick={startGame}
              sx={{ scale: '1.5', background: 'linear-gradient(45deg, #f44336, #ff9800)' }}
            >
              Start Game
            </Button>
          </Box>
        )}

        {gameState.countdown !== null && (
          <CountdownOverlay>
            <Typography variant="h1" color="white" sx={{ fontWeight: 'bold', textShadow: '0 0 20px #00d2ff' }}>
              {gameState.countdown === 0 ? "GO!" : gameState.countdown}
            </Typography>
          </CountdownOverlay>
        )}

        {gameState.isPlaying && gameState.countdown === null && (
          <>
            {/* Normál célpont (Adamantine-on kívül) */}
            {gameMode !== "AdamantineParagon" && (
              <Target
                size={settings.size as 'small' | 'medium' | 'large'}
                style={{ 
                  left: `${targetPosition.x}%`, 
                  top: `${targetPosition.y}%`
                }}
                onClick={handleTargetClick}
                sx={{ color: '#00d2ff', filter: 'drop-shadow(0 0 10px #00d2ff)' }}
              >
                <FiTarget />
              </Target>
            )}

            {/* Adamantine specifikus célpontok */}
            {gameMode === "AdamantineParagon" && adamantineTargets.map(target => (
              <Target
                key={target.id}
                style={{ left: `${target.position.x}%`, top: `${target.position.y}%` }}
                onClick={() => handleAdamantineClick(target)}
                size="medium"
                sx={{ color: "#2196f3", filter: 'drop-shadow(0 0 8px #2196f3)' }}
              >
                <FiTarget />
              </Target>
            ))}

            {/* Speciális ikonok (Csillag, Token, stb.) */}
            {renderSpecialIcons()}
          </>
        )}
      </GameArea>

      {/* Game Over Dialog */}
      <AnimatedDialog open={gameOverDialogOpen} TransitionComponent={Zoom} darkMode={darkMode}>
        <DialogTitle sx={{ textAlign: 'center', color: '#ff4b2b', fontWeight: 'bold' }}>Game Over!</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ my: 2 }}>{gameState.score}</Typography>
          <Typography variant="body1">Points Earned</Typography>
          {(gameMode === "AdamantineParagon" || gameMode === "AmberSentinel") && (
            <Typography sx={{ mt: 2, color: '#4caf50', fontWeight: 'bold' }}>
              Tokens Collected: {tokens}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setGameOverDialogOpen(false)} sx={{ color: darkMode ? 'white' : 'inherit' }}>Close</Button>
          <Button onClick={resetGame} variant="contained" sx={{ background: '#ff4b2b' }}>Play Again</Button>
        </DialogActions>
      </AnimatedDialog>
    </GameContainer>
  );
};

export default AimTesterGame;